import dayjs from '@/js/libs/dayjs';
import type { DateRangeType } from '@/js/libs/ui/src/V2/Form/DatePicker/types';

import { DATE_PARAMETER_FORMAT } from 'src/constants';
import type { Option } from 'src/types';
import { isEnumValue } from 'src/utils/isEnumValue';
import type { StorageScope } from '../useSessionStorage';
import type { FilterConfig, FilterValue } from './types';

interface CustomId {
  _id: string;
  toString: () => string;
}

export type ConfigOptions<TValue extends FilterValue> = { defaultValue?: TValue; hidden?: boolean; customScope?: StorageScope } & (
  | { isMainFilter: true; getLabel?: never; isSelectedByDefault?: never }
  | { isMainFilter?: never; getLabel: () => string; isSelectedByDefault?: boolean }
);
export type NonnullableConfigOptions<TValue extends FilterValue> = {
  defaultValue: TValue;
  hidden?: boolean;
  customScope?: StorageScope;
} & (
  | { isMainFilter: true; getLabel?: never; isSelectedByDefault?: never }
  | { isMainFilter?: never; getLabel: () => string; isSelectedByDefault?: boolean }
);

export const createStringFilterConfig = (options: ConfigOptions<string> = { isMainFilter: true }): FilterConfig<string> => {
  const { defaultValue = '', getLabel, isMainFilter, hidden, isSelectedByDefault, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: '',
    serialize: (value: string) => value || null,
    syncDeserialize: (stored: string | null) => stored ?? null,
    compare: (a: string, b: string) => a === b,
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
    isSelectedByDefault,
  };
};

export const createEnumFilterConfig = <TEnum extends Record<string, string>>(
  enumObj: TEnum,
  options: ConfigOptions<TEnum[keyof TEnum] | null> = { isMainFilter: true },
): FilterConfig<TEnum[keyof TEnum] | null> => {
  const { defaultValue = null, getLabel, isMainFilter, hidden, isSelectedByDefault, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: null,
    serialize: (value: TEnum[keyof TEnum] | null) => value,
    syncDeserialize: (stored: string | null) => (isEnumValue(enumObj, stored) ? stored : null),
    compare: (a: string | null, b: string | null) => a === b,
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
    isSelectedByDefault,
  };
};

export const createBooleanFilterConfig = (options: ConfigOptions<boolean> = { isMainFilter: true }): FilterConfig<boolean> => {
  const { defaultValue = false, getLabel, isMainFilter, hidden, isSelectedByDefault, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: false,
    serialize: (value: boolean) => (value ? 'true' : null),
    syncDeserialize: (stored: string | null) => Boolean(stored),
    compare: (a: boolean, b: boolean) => a === b,
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
    isSelectedByDefault,
  };
};

export const createDateRangeFilterConfig = (
  options: ConfigOptions<DateRangeType | null> = { isMainFilter: true },
): FilterConfig<DateRangeType | null> => {
  const { defaultValue = null, getLabel, isMainFilter, hidden, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: null,
    serialize: (value: DateRangeType | null) => {
      if (!value) return null;

      const startDate = value[0] ? dayjs(value[0]).format(DATE_PARAMETER_FORMAT) : null;
      const endDate = value[1] ? dayjs(value[1]).format(DATE_PARAMETER_FORMAT) : null;

      if (startDate && endDate) {
        return `${startDate},${endDate}`;
      }

      if (startDate) {
        return startDate;
      }

      if (endDate) {
        return endDate;
      }

      return null;
    },
    syncDeserialize: (stored: string | null) => {
      if (!stored) return null;

      try {
        // Check if it contains a comma (date range)
        if (stored.includes(',')) {
          const split = stored.split(',');
          if (split.length !== 2) return null;

          const [startStr, endStr] = split;
          const startDate = startStr ? dayjs(startStr).toDate() : null;
          const endDate = endStr ? dayjs(endStr).toDate() : null;

          return [dayjs(startDate).isValid() ? startDate : null, dayjs(endDate).isValid() ? endDate : null];
        }
        // Single date (assume it's start date)
        const date = dayjs(stored).toDate();
        return dayjs(date).isValid() ? [date, null] : null;
      } catch {
        return null;
      }
    },
    compare: (a: DateRangeType | null, b: DateRangeType | null) => {
      if (a === null || b === null) {
        return a === b;
      }
      const [aStart, aEnd] = a;
      const [bStart, bEnd] = b;
      return dayjs(aStart).isSame(bStart, 'day') && dayjs(aEnd).isSame(bEnd, 'day');
    },
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
  };
};

export const createSingleNullableEnumOptionFilterConfig = <T extends string | number>(
  enumObj: Record<string, T>,
  options: ConfigOptions<Option<T> | null> = { isMainFilter: true },
): FilterConfig<Option<T> | null> => {
  const { defaultValue = null, isMainFilter, getLabel, hidden, isSelectedByDefault, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: null,
    serialize: (option: Option<T> | null) => (option?.value ? String(option.value) : null),
    syncDeserialize: (stored: string | null) => (isEnumValue(enumObj, stored) ? { label: stored, value: stored } : null),
    compare: (a: Option<T> | null, b: Option<T> | null) => a?.value === b?.value,
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
    isSelectedByDefault,
  };
};

export const createSingleNonnullableEnumOptionFilterConfig = <T extends string | number>(
  enumObj: Record<string, T>,
  options: NonnullableConfigOptions<Option<T>>,
): FilterConfig<Option<T>> => {
  const { defaultValue, isMainFilter, getLabel, hidden, isSelectedByDefault, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: defaultValue,
    // eslint-disable-next-line no-constant-binary-expression
    serialize: (option: Option<T>) => String(option.value) ?? null,
    syncDeserialize: (stored: string | null) => (isEnumValue(enumObj, stored) ? { label: stored, value: stored } : null),
    compare: (a: Option<T>, b: Option<T>) => a.value === b.value,
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
    isSelectedByDefault,
  };
};

export const createMultiEnumOptionFilterConfig = <T extends string | number>(
  enumObj: Record<string, T>,
  options: ConfigOptions<Option<T>[]> = { isMainFilter: true },
): FilterConfig<Option<T>[]> => {
  const { defaultValue = [], isMainFilter, getLabel, hidden, isSelectedByDefault, customScope } = options;

  const config = {
    defaultValue,
    emptyValue: [],
    serialize: (options: Option<T>[]) => (options.length > 0 ? options.map((option) => option.value).join(',') : null),
    syncDeserialize: (stored: string | null) => {
      const split = stored?.split(',').filter((value) => isEnumValue(enumObj, value));
      if (!split || !split.length) return null;
      return split.map((value) => ({ label: value, value }));
    },
    compare: (a: Option<T>[], b: Option<T>[]) => {
      const aSet = new Set(a.map((option) => option.value));
      const bSet = new Set(b.map((option) => option.value));

      if (aSet.size !== bSet.size) return false;

      for (const v of aSet) {
        if (!bSet.has(v)) return false;
      }

      return true;
    },
    hidden,
    customScope,
  };

  if (isMainFilter) {
    return {
      ...config,
      isMainFilter,
    };
  }

  return {
    ...config,
    getLabel,
    isSelectedByDefault,
  };
};

type MultiOptionFilterConfigBaseOptions<N extends boolean, T extends string | number | CustomId> = (N extends true
  ? ConfigOptions<Option<T>[] | null>
  : ConfigOptions<Option<T>[]>) & {
  serialize?: (value: N extends true ? Option<T>[] | null : Option<T>[]) => string | null;
  compare?: (a: N extends true ? Option<T>[] | null : Option<T>[], b: N extends true ? Option<T>[] | null : Option<T>[]) => boolean;
};

type SyncMultiOptionFilterConfigOptions<N extends boolean, T extends string | number | CustomId> = MultiOptionFilterConfigBaseOptions<
  N,
  T
> & {
  syncDeserialize?: (
    stored: string | null,
    persistedValues: Record<string, string | null>,
  ) => (N extends true ? Option<T>[] | null : Option<T>[]) | null;
  asyncDeserialize?: never;
};

type AsyncMultiOptionFilterConfigOptions<N extends boolean, T extends string | number | CustomId> = MultiOptionFilterConfigBaseOptions<
  N,
  T
> & {
  asyncDeserialize?: (
    stored: string,
    persistedValues: Record<string, string | null>,
  ) => Promise<(N extends true ? Option<T>[] | null : Option<T>[]) | null>;
  syncDeserialize?: never;
};

type MultiOptionFilterConfigOptions<N extends boolean, T extends string | number | CustomId> =
  | SyncMultiOptionFilterConfigOptions<N, T>
  | AsyncMultiOptionFilterConfigOptions<N, T>;

const createMultiOptionFilterConfigCommon = <N extends boolean, T extends string | number | CustomId>(
  nullable: N,
  options: MultiOptionFilterConfigOptions<N, T> = { isMainFilter: true },
): FilterConfig<N extends true ? Option<T>[] | null : Option<T>[]> => {
  const {
    defaultValue,
    isMainFilter,
    getLabel,
    hidden,
    isSelectedByDefault,
    serialize,
    compare,
    syncDeserialize,
    asyncDeserialize,
    customScope,
  } = options;

  const finalDefaultValue = (defaultValue ?? (nullable ? null : [])) as N extends true ? Option<T>[] | null : Option<T>[];

  const defaultSerialize = (value: N extends true ? Option<T>[] | null : Option<T>[]) => {
    const options = value || [];
    return options.length > 0 ? options.map((option) => option.value).join(',') : null;
  };

  const defaultSyncDeserialize = (stored: string | null) => {
    const split = stored?.split(',').filter((value) => !!value);
    if (!split?.length) return null;
    return split.map((value) => ({ label: value, value })) as N extends true ? Option<T>[] | null : Option<T>[];
  };

  const defaultCompare = (a: N extends true ? Option<T>[] | null : Option<T>[], b: N extends true ? Option<T>[] | null : Option<T>[]) => {
    const aArray = a || [];
    const bArray = b || [];
    const aSet = new Set(aArray.map((option) => option.value));
    const bSet = new Set(bArray.map((option) => option.value));

    if (aSet.size !== bSet.size) return false;

    for (const v of aSet) {
      if (!bSet.has(v)) return false;
    }

    return true;
  };

  const config = {
    defaultValue: finalDefaultValue,
    emptyValue: (nullable ? null : []) as N extends true ? Option<T>[] | null : Option<T>[],
    serialize: serialize ?? defaultSerialize,
    compare: compare ?? defaultCompare,
    hidden,
    customScope,
  };

  if (asyncDeserialize) {
    const asyncConfig = {
      ...config,
      asyncDeserialize,
    };

    if (isMainFilter) {
      return {
        ...asyncConfig,
        isMainFilter,
      };
    }

    return {
      ...asyncConfig,
      getLabel,
      isSelectedByDefault,
    };
  }

  const syncConfig = {
    ...config,
    syncDeserialize: syncDeserialize ?? defaultSyncDeserialize,
  };

  if (isMainFilter) {
    return {
      ...syncConfig,
      isMainFilter,
    };
  }

  return {
    ...syncConfig,
    getLabel,
    isSelectedByDefault,
  };
};

export const createMultiOptionFilterConfigNonNullable = <T extends string | number | CustomId>(
  options?: MultiOptionFilterConfigOptions<false, T>,
) => createMultiOptionFilterConfigCommon(false, options);

export const createMultiOptionFilterConfig = <T extends string | number | CustomId>(options?: MultiOptionFilterConfigOptions<true, T>) =>
  createMultiOptionFilterConfigCommon(true, options);
